import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Switch } from '../ui/switch';
import { Plus, Save, Trash2, RotateCcw, Loader2, Info, Eye } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PROPERTY_TYPE_LABELS = {
  appartement: 'Appartement',
  maison: 'Maison / Villa',
  immeuble: 'Immeuble',
  terrain: 'Terrain',
  local_commercial: 'Local Commercial',
  bureau: 'Bureau',
  entrepot: 'Entrepôt',
  parking: 'Parking',
};

const INPUT_TYPE_OPTIONS = [
  { value: '', label: 'Simple case à cocher' },
  { value: 'quantity', label: 'Quantité (nombre)' },
  { value: 'surface', label: 'Surface (m²)' },
];

export default function EstimationCoefsTab() {
  const [coefs, setCoefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRow, setEditingRow] = useState(null); // { id, ...fields }
  const [saving, setSaving] = useState(false);
  const [addingFor, setAddingFor] = useState(null); // property_type we're adding to
  const [newRow, setNewRow] = useState({ feature_id: '', label: '', coef: 0, icon: '', input_type: '' });

  // Vue coefficients state
  const [vueCats, setVueCats] = useState([]);
  const [editingVue, setEditingVue] = useState(null); // { catId, subs: [...] }
  const [savingVue, setSavingVue] = useState(false);

  const fetchCoefs = async () => {
    try {
      const token = localStorage.getItem('token');
      const [coefsRes, vueRes] = await Promise.all([
        axios.get(`${API}/api/admin/estimation-coefficients`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/admin/vue-coefficients`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setCoefs(coefsRes.data.coefficients || []);
      setVueCats(vueRes.data || []);
    } catch (e) {
      toast.error('Erreur lors du chargement des coefficients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCoefs(); }, []);

  const token = () => localStorage.getItem('token');

  const toggleActive = async (coef) => {
    try {
      await axios.put(`${API}/api/admin/estimation-coefficients/${coef.id}`,
        { active: !coef.active },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setCoefs(prev => prev.map(c => c.id === coef.id ? { ...c, active: !c.active } : c));
      toast.success(`${coef.label} ${!coef.active ? 'activé' : 'désactivé'}`);
    } catch (e) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const saveRow = async () => {
    if (!editingRow) return;
    setSaving(true);
    try {
      const payload = {
        label: editingRow.label,
        coef: parseFloat(editingRow.coef),
        icon: editingRow.icon,
        input_type: editingRow.input_type || null,
      };
      await axios.put(`${API}/api/admin/estimation-coefficients/${editingRow.id}`, payload, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      setCoefs(prev => prev.map(c => c.id === editingRow.id ? { ...c, ...payload } : c));
      setEditingRow(null);
      toast.success('Modifications enregistrées');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const deleteRow = async (coef) => {
    if (!window.confirm(`Supprimer définitivement "${coef.label}" ?`)) return;
    try {
      await axios.delete(`${API}/api/admin/estimation-coefficients/${coef.id}`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      setCoefs(prev => prev.filter(c => c.id !== coef.id));
      toast.success('Supprimé');
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const addRow = async (property_type) => {
    if (!newRow.label || !newRow.feature_id) {
      toast.error('Feature ID et Label requis');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/admin/estimation-coefficients`, {
        property_type,
        feature_id: newRow.feature_id.trim().toLowerCase().replace(/\s+/g, '_'),
        label: newRow.label,
        coef: parseFloat(newRow.coef) || 0,
        icon: newRow.icon,
        input_type: newRow.input_type || null,
      }, { headers: { Authorization: `Bearer ${token()}` } });
      setCoefs(prev => [...prev, res.data]);
      setAddingFor(null);
      setNewRow({ feature_id: '', label: '', coef: 0, icon: '', input_type: '' });
      toast.success('Caractéristique ajoutée');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm('Remettre les coefficients par défaut ? Cela supprimera TOUTES vos modifications personnalisées.')) return;
    try {
      await axios.post(`${API}/api/admin/estimation-coefficients/reset-defaults`, {}, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      await fetchCoefs();
      toast.success('Coefficients réinitialisés');
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const startEditVue = (cat) => {
    setEditingVue({ catId: cat.id, subs: cat.subs.map(s => ({ ...s })) });
  };

  const saveVue = async () => {
    if (!editingVue) return;
    setSavingVue(true);
    try {
      await axios.put(`${API}/api/admin/vue-coefficients/${editingVue.catId}`,
        { subs: editingVue.subs },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setVueCats(prev => prev.map(c => c.id === editingVue.catId ? { ...c, subs: editingVue.subs } : c));
      setEditingVue(null);
      toast.success('Coefficients Vue mis à jour');
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingVue(false);
    }
  };

  const resetVueDefaults = async () => {
    if (!window.confirm('Remettre les coefficients Vue par défaut ?')) return;
    try {
      await axios.post(`${API}/api/admin/vue-coefficients/reset-defaults`, {}, { headers: { Authorization: `Bearer ${token()}` } });
      await fetchCoefs();
      toast.success('Coefficients Vue réinitialisés');
    } catch (e) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gold" /></div>;
  }

  const grouped = coefs.reduce((acc, c) => {
    (acc[c.property_type] = acc[c.property_type] || []).push(c);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="estimation-coefs-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-navy">Coefficients d'Estimation</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Modifiez dynamiquement l'impact de chaque caractéristique sur le prix d'estimation.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={resetDefaults}
          className="border-red-300 text-red-700 hover:bg-red-50"
          data-testid="reset-defaults-btn"
        >
          <RotateCcw className="w-4 h-4 mr-2" /> Remettre par défaut
        </Button>
      </div>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-1">Comment ça marche :</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Coef %</strong> : impact sur le prix final (ex : 5 = +5%, -15 = -15%)</li>
              <li><strong>Case à cocher</strong> : le client coche ou non (ex : Rénové)</li>
              <li><strong>Quantité</strong> : le client saisit un nombre (ex : 2 parkings → coef × 2)</li>
              <li><strong>Surface</strong> : le client saisit des m² (ex : Balcon 15m² → coef × 15)</li>
              <li>Désactiver une caractéristique la masque du formulaire client sans la supprimer</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {Object.keys(PROPERTY_TYPE_LABELS).map(pt => (
        <Card key={pt} data-testid={`coefs-${pt}`}>
          <CardContent className="p-0">
            <div className="px-4 py-3 bg-navy text-white rounded-t-lg flex items-center justify-between flex-wrap gap-2">
              <h3 className="font-bold">{PROPERTY_TYPE_LABELS[pt]}</h3>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setAddingFor(pt); setNewRow({ feature_id: '', label: '', coef: 0, icon: '', input_type: '' }); }}
                className="bg-gold hover:bg-gold/90 text-black border-gold"
                data-testid={`add-coef-${pt}`}
              >
                <Plus className="w-4 h-4 mr-1" /> Ajouter
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 text-xs">
                  <tr>
                    <th className="px-3 py-2 text-left w-10">Icône</th>
                    <th className="px-3 py-2 text-left">Libellé (client)</th>
                    <th className="px-3 py-2 text-left">ID (technique)</th>
                    <th className="px-3 py-2 text-center w-24">Coef %</th>
                    <th className="px-3 py-2 text-left w-36">Type d'input</th>
                    <th className="px-3 py-2 text-center w-20">Actif</th>
                    <th className="px-3 py-2 text-right w-40">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(grouped[pt] || []).map(c => {
                    const isEditing = editingRow?.id === c.id;
                    return (
                      <tr key={c.id} className="border-t hover:bg-gray-50" data-testid={`coef-row-${c.id}`}>
                        <td className="px-3 py-2 text-xl text-center">
                          {isEditing ? (
                            <Input value={editingRow.icon || ''} onChange={(e) => setEditingRow({ ...editingRow, icon: e.target.value })} className="w-14 text-center" />
                          ) : c.icon}
                        </td>
                        <td className="px-3 py-2">
                          {isEditing ? (
                            <Input value={editingRow.label} onChange={(e) => setEditingRow({ ...editingRow, label: e.target.value })} />
                          ) : c.label}
                        </td>
                        <td className="px-3 py-2 text-xs font-mono text-muted-foreground">{c.feature_id}</td>
                        <td className="px-3 py-2 text-center">
                          {isEditing ? (
                            <Input type="number" step="0.5" value={editingRow.coef} onChange={(e) => setEditingRow({ ...editingRow, coef: e.target.value })} className="w-20 text-center" />
                          ) : (
                            <span className={`font-bold ${c.coef >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                              {c.coef > 0 ? '+' : ''}{c.coef}%
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {isEditing ? (
                            <select value={editingRow.input_type || ''} onChange={(e) => setEditingRow({ ...editingRow, input_type: e.target.value })} className="border rounded px-2 py-1 text-sm w-full">
                              {INPUT_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              {c.input_type === 'quantity' ? 'Quantité' : c.input_type === 'surface' ? 'Surface m²' : 'Case à cocher'}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Switch checked={c.active} onCheckedChange={() => toggleActive(c)} data-testid={`toggle-${c.id}`} />
                        </td>
                        <td className="px-3 py-2 text-right">
                          {isEditing ? (
                            <div className="flex gap-1 justify-end">
                              <Button size="sm" onClick={saveRow} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`save-${c.id}`}>
                                <Save className="w-3 h-3 mr-1" /> Sauver
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingRow(null)}>Annuler</Button>
                            </div>
                          ) : (
                            <div className="flex gap-1 justify-end">
                              <Button size="sm" variant="outline" onClick={() => setEditingRow({ ...c })} data-testid={`edit-${c.id}`}>
                                Modifier
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => deleteRow(c)} className="border-red-300 text-red-700 hover:bg-red-50" data-testid={`delete-${c.id}`}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {addingFor === pt && (
                    <tr className="bg-gold/10 border-t" data-testid={`add-row-${pt}`}>
                      <td className="px-3 py-2">
                        <Input value={newRow.icon} onChange={(e) => setNewRow({ ...newRow, icon: e.target.value })} placeholder="🌿" className="w-14 text-center" />
                      </td>
                      <td className="px-3 py-2">
                        <Input value={newRow.label} onChange={(e) => setNewRow({ ...newRow, label: e.target.value })} placeholder="Ex: Terrasse arborée" data-testid="new-label-input" />
                      </td>
                      <td className="px-3 py-2">
                        <Input value={newRow.feature_id} onChange={(e) => setNewRow({ ...newRow, feature_id: e.target.value })} placeholder="ex: terrasse_arboree" className="font-mono text-xs" data-testid="new-id-input" />
                      </td>
                      <td className="px-3 py-2">
                        <Input type="number" step="0.5" value={newRow.coef} onChange={(e) => setNewRow({ ...newRow, coef: e.target.value })} className="w-20 text-center" />
                      </td>
                      <td className="px-3 py-2">
                        <select value={newRow.input_type} onChange={(e) => setNewRow({ ...newRow, input_type: e.target.value })} className="border rounded px-2 py-1 text-sm w-full">
                          {INPUT_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className="text-xs text-muted-foreground">Actif par défaut</span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" onClick={() => addRow(pt)} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`confirm-add-${pt}`}>
                            <Plus className="w-3 h-3 mr-1" /> Ajouter
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setAddingFor(null)}>Annuler</Button>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* ===== TYPE DE VUE ===== */}
      <div className="mt-2">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div>
            <h2 className="text-xl font-bold text-navy">Coefficients Type de Vue</h2>
            <p className="text-sm text-muted-foreground mt-1">Modifiez les % de plus-value par type de vue. Invisible pour le client, visible ici et dans l'admin partenaire.</p>
          </div>
          <Button variant="outline" onClick={resetVueDefaults} className="border-red-300 text-red-700 hover:bg-red-50" size="sm">
            <RotateCcw className="w-4 h-4 mr-2" /> Réinitialiser Vue
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {vueCats.map(cat => {
            const isEditing = editingVue?.catId === cat.id;
            const subs = isEditing ? editingVue.subs : cat.subs;
            return (
              <Card key={cat.id} className="border-2 border-gray-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="text-lg mr-2">{cat.category_icon}</span>
                      <span className="font-bold text-navy">{cat.category_label}</span>
                      <p className="text-xs text-muted-foreground mt-0.5">{cat.category_subtitle}</p>
                    </div>
                    {!isEditing ? (
                      <Button size="sm" variant="outline" onClick={() => startEditVue(cat)} data-testid={`edit-vue-${cat.category_id}`}>
                        Modifier
                      </Button>
                    ) : (
                      <div className="flex gap-1">
                        <Button size="sm" onClick={saveVue} disabled={savingVue} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`save-vue-${cat.category_id}`}>
                          <Save className="w-3 h-3 mr-1" /> Sauver
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingVue(null)}>Annuler</Button>
                      </div>
                    )}
                  </div>
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-1.5 text-left text-xs font-medium text-muted-foreground">Sous-option</th>
                        <th className="px-2 py-1.5 text-center text-xs font-medium text-muted-foreground w-24">Coef %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subs.map((sub, idx) => (
                        <tr key={sub.sub_id} className="border-t">
                          <td className="px-2 py-2 text-sm">{sub.sub_label}</td>
                          <td className="px-2 py-2 text-center">
                            {isEditing ? (
                              <Input
                                type="number"
                                step="1"
                                value={editingVue.subs[idx].coef}
                                onChange={(e) => {
                                  const updated = [...editingVue.subs];
                                  updated[idx] = { ...updated[idx], coef: parseFloat(e.target.value) || 0 };
                                  setEditingVue({ ...editingVue, subs: updated });
                                }}
                                className="w-20 text-center mx-auto"
                                data-testid={`vue-coef-input-${sub.sub_id}`}
                              />
                            ) : (
                              <span className={`font-bold ${sub.coef > 0 ? 'text-emerald-700' : sub.coef < 0 ? 'text-red-700' : 'text-gray-500'}`}>
                                {sub.coef > 0 ? '+' : ''}{sub.coef}%
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
